import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../../contexts/AuthContext';
import { useLocation } from 'react-router-dom';
import MemberParticipationChart from './MemberParticipationChart';
import { getCourseId } from '../../../firebase';
import MyHeader from '../../../components/common/admin/MyHeader';
import CollaborationNetworkGraph from './CollaborationNetworkGraph';
import GenericBoxPlot from './GenericBoxPlot';
import WeeklyActivitiesChart from './WeeklyActivitiesChart';
import DailyFrequencyChart from './DailyFrequencyChart';
import TrendLineChart from './TrendLineChart';
import AdminDataService from '../../../services/AdminDataService';
const AdminTeamDynamics = () => {
    const location = useLocation();
    const { groupIndex, groups, allEvents } = location.state || {};
    const { groupInfo, groupMembers: authGroupMembers } = useAuth();
    const currentGroup = groups?.[groupIndex];
    const groupMembers = currentGroup?.members || authGroupMembers || [];
    const [activitiesData, setActivitiesData] = useState(allEvents || []);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [courseTeamData, setCourseTeamData] = useState(null);
    const [currentGroupActivitiesCount, setCurrentGroupActivitiesCount] = useState(0);
    const [currentGroupWorkingHours, setCurrentGroupWorkingHours] = useState(0);
    const [currentGroupArtifactsCount, setCurrentGroupArtifactsCount] = useState(0);
    const [selectedMetric, setSelectedMetric] = useState('activities'); // Default to activities metric
    const [courseId, setCourseId] = useState(null);

    // Derived state using useMemo
    const allGroupsActivitiesCounts = useMemo(() => {
        return courseTeamData?.activitiesCounts || [];
    }, [courseTeamData]);

    const allGroupsWorkingHours = useMemo(() => {
        return courseTeamData?.workingHours || [];
    }, [courseTeamData]);

    const allGroupsArtifactsCounts = useMemo(() => {
        return courseTeamData?.artifactsCounts || [];
    }, [courseTeamData]);

    useEffect(() => {
        const loadData = async () => {
            if (!groupInfo || !groupInfo.groupId) return;

            setLoading(true);
            setError(null);

            try {
                const currentCourseId = await getCourseId(groupInfo.groupId);
                if (!currentCourseId) {
                    throw new Error("No course ID found for this group");
                }
                setCourseId(currentCourseId);

                const [groupEventsData, teamDynamicsData] = await Promise.all([
                    AdminDataService.getGroupEvents(groupInfo.groupId, !allEvents?.length),
                    AdminDataService.getCourseTeamDynamicsData(currentCourseId)
                ]);

                const eventsSource = allEvents?.length > 0 ? allEvents : groupEventsData.events;
                setActivitiesData(eventsSource);
                setCurrentGroupActivitiesCount(eventsSource.length);
                setCurrentGroupWorkingHours(groupEventsData.workingHours);
                setCurrentGroupArtifactsCount(calculateArtifactsCount(groupEventsData.events || []));
                setCourseTeamData(teamDynamicsData);
            } catch (error) {
                console.error("Error loading team dynamics data:", error);
                setError(error.message || "Failed to load data");
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [groupInfo]);

    // Function to calculate artifacts count from events
    const calculateArtifactsCount = (events) => {
        let count = 0;
        events.forEach(event => {
            // Count uploaded files as artifacts
            if (event.files && Array.isArray(event.files)) {
                count += event.files.length;
            }
            // You can add other artifact types here if needed
        });
        return count;
    };

    // Handler for boxplot clicks
    const handleBoxPlotClick = (metric) => {
        if (metric !== selectedMetric) {
            const bottomSection = document.querySelector('.bottom-right-section');
            const prevHeight = bottomSection ? bottomSection.offsetHeight : 0;

            setSelectedMetric(metric);

            if (bottomSection && prevHeight > 0) {
                requestAnimationFrame(() => {
                    bottomSection.style.height = `${prevHeight}px`;
                });
            }
        }
    };

    return (
        <>
            <MyHeader groupIndex={groupIndex} groups={groups} />
            <PageWrapper>
                <MainContainer>
                    {loading ? (
                        <LoadingContainer>Loading activity data...</LoadingContainer>
                    ) : error ? (
                        <ErrorContainer>
                            Error loading data: {error}. Please try again later.
                        </ErrorContainer>
                    ) : activitiesData.length === 0 ? (
                        <NoDataContainer>
                            No activities found for this group. Team dynamics cannot be generated.
                        </NoDataContainer>
                    ) : (
                        <DashboardLayout>
                            <LeftPanel>
                                <CollabNetworkContainer>
                                    <CollaborationNetworkGraph
                                        activitiesData={activitiesData}
                                    />
                                </CollabNetworkContainer>
                            </LeftPanel>

                            <RightContainer>
                                <TopRightSection>
                                    <DiagramsPanel>
                                        <GridItem>
                                            <MemberParticipationChart activitiesData={activitiesData} mode="self"/>
                                        </GridItem>
                                        <GridItem>
                                            <MemberParticipationChart activitiesData={activitiesData} mode="pair" />
                                        </GridItem>
                                        <GridItem>
                                            <MemberParticipationChart activitiesData={activitiesData} mode="team" />
                                        </GridItem>
                                        <GridItem>
                                            <BoxPlotContainer>
                                                <GenericBoxPlot
                                                    currentGroupValue={currentGroupActivitiesCount}
                                                    allGroupsValues={allGroupsActivitiesCounts}
                                                    metric="activities"
                                                />
                                            </BoxPlotContainer>
                                        </GridItem>
                                        <GridItem>
                                            <BoxPlotContainer>
                                                <GenericBoxPlot
                                                    currentGroupValue={currentGroupWorkingHours}
                                                    allGroupsValues={allGroupsWorkingHours}
                                                    metric="workingHours"
                                                />
                                            </BoxPlotContainer>
                                        </GridItem>
                                        <GridItem>
                                            <BoxPlotContainer>
                                                <GenericBoxPlot
                                                    currentGroupValue={currentGroupArtifactsCount}
                                                    allGroupsValues={allGroupsArtifactsCounts}
                                                    metric="artifacts"
                                                />
                                            </BoxPlotContainer>
                                        </GridItem>
                                    </DiagramsPanel>
                                </TopRightSection>

                                <BottomRightSection className="bottom-right-section">
                                    <BarChartPanel>
                                        {activitiesData.length > 0 ? (
                                            <WeeklyActivitiesChart
                                                activitiesData={activitiesData}
                                                key={`weekly-chart`}
                                            />
                                        ) : (
                                            <PlaceholderText>No data available for weekly activities chart</PlaceholderText>
                                        )}
                                    </BarChartPanel>

                                    <BarChartPanel>
                                        {activitiesData.length > 0 ? (
                                            <DailyFrequencyChart
                                                activitiesData={activitiesData}
                                                key={`daily-chart`}
                                            />
                                        ) : (
                                            <PlaceholderText>No data available for daily frequency chart</PlaceholderText>
                                        )}
                                    </BarChartPanel>
                                </BottomRightSection>
                            </RightContainer>
                        </DashboardLayout>
                    )}
                </MainContainer>
            </PageWrapper>
        </>
    );
};

const PageWrapper = styled.div`
    background-color: #fff;
    display: flex;
    flex-direction: column;
    padding: 15px 20px 80px;
    min-height: 100vh;
`;

const MainContainer = styled.div`
    padding: 13px 0px 0px;
    flex: 1;
`;

const LoadingContainer = styled.div`
    padding: 30px;
    text-align: center;
    font-size: 16px;
    color: #666;
`;

const ErrorContainer = styled.div`
    padding: 30px;
    text-align: center;
    font-size: 16px;
    color: #e63946;
    background-color: #f8d7da;
    border-radius: 8px;
    margin: 20px 0;
`;

const NoDataContainer = styled.div`
    padding: 30px;
    text-align: center;
    font-size: 16px;
    color: #666;
    background-color: #f9f9f9;
    border-radius: 8px;
    margin: 20px 0;
`;

const DashboardLayout = styled.div`
    display: flex;
    gap: 20px;
    width: 100%;
    height: calc(100vh - 150px);
`;

const LeftPanel = styled.div`
    flex: 1;
    border: 1px solid #ccc;
    border-radius: 4px;
    padding: 15px;
    display: flex;
    flex-direction: column;
    background-color: #f0f0f0;
`;

const CollabNetworkContainer = styled.div`
    flex: 1;
    width: 100%;
    height: 100%;
`;

const RightContainer = styled.div`
    flex: 2;
    display: flex;
    flex-direction: column;
    gap: 20px;
    height: 100%;
`;

const TopRightSection = styled.div`
    height: calc(50% - 10px);
    display: flex;
    gap: 20px;
    min-height: 0;
`;


const DiagramsPanel = styled.div`
    flex: 1.3;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: repeat(2, 1fr); 
    height: 100%;    
    gap: 8px;
`;

const BottomRightSection = styled.div`
    height: calc(50% - 10px);
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: 1fr 1fr;
    gap: 10px;
    min-height: 0;
    overflow: visible;
`;

const GridItem = styled.div`
    border: 1px solid #ccc;
    border-radius: 4px;
    display: flex;
    padding-top: 8px;
    flex-direction: column;
    background-color: #f0f0f0;
    transition: all 0.2s ease-in-out;
    cursor: pointer;
    height: 100%;
    min-height: 0;
    
    &.selected {
        border: 2px solid #4285f4;
        box-shadow: 0 0 5px rgba(66, 133, 244, 0.5);
    }
    
    &:hover {
        box-shadow: 0 0 8px rgba(0, 0, 0, 0.1);
    }
    
    &:last-child {
        cursor: default;
        &:hover {
            box-shadow: none;
        }
    }
`;

const BoxPlotContainer = styled.div`
    flex: 1;
    display: flex;
    min-height: 0;
    width: 100%;
`;

const BarChartPanel = styled.div`
    position: relative;
    min-height: 0;
    flex: 1;
    border: 1px solid #ccc;
    border-radius: 4px;
    display: flex;
    flex-direction: column;
    overflow: visible;
    height: 100%;
    box-sizing: border-box;
    background-color: #f0f0f0;
`;

const PlaceholderText = styled.div`
    color: #999;
    font-size: 16px;
    text-align: center;
`;

export default AdminTeamDynamics;